/**
 * Order Positive Rational Numbers generator (6.NR.1.3).
 *
 * Presents 3-4 distinct positive rationals (as fractions and/or decimals) in a
 * scrambled order and asks the student to list them from least to greatest. The
 * answer is a canonical SetAnswer (deduped, sorted ascending by makeSetAnswer),
 * so the accepted submission is the values in ascending order. Values are drawn
 * from SC-allowed denominators and kept distinct so the set never collapses
 * below the requested count.
 *
 * Families:
 *   F1: three values
 *   F2: four values
 *   F3: mixed forms (fractions and decimals shown together)
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import { makeSetAnswer } from "../engine/answer";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "order-rationals" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3";

interface Cfg {
  readonly families: readonly Family[];
  readonly denoms: readonly number[];
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1"], denoms: [2, 4, 5, 10] },
  standard: { families: ["F1", "F2"], denoms: [2, 4, 5, 8, 10, 20] },
  hard: { families: ["F1", "F2", "F3"], denoms: [4, 8, 20, 25, 50] },
  challenge: { families: ["F2", "F3"], denoms: [8, 20, 25, 50, 100] },
};

function placesFor(den: number): number {
  let d = den;
  let twos = 0;
  let fives = 0;
  while (d % 2 === 0) {
    d /= 2;
    twos++;
  }
  while (d % 5 === 0) {
    d /= 5;
    fives++;
  }
  return Math.max(twos, fives);
}

function decStr(v: Rational): string {
  return v.toNumber().toFixed(placesFor(Number(v.d)));
}

/** Pick `count` DISTINCT positive rationals from SC denominators. */
function distinctRationals(rng: Rng, denoms: readonly number[], count: number): Rational[] {
  const seen = new Set<string>();
  const values: Rational[] = [];
  let guard = 0;
  while (values.length < count && guard < 500) {
    guard++;
    const den = rng.pick(denoms);
    const num = rng.int(1, den + Math.floor(den / 2));
    const v = rat(num, den);
    if (!seen.has(v.key())) {
      seen.add(v.key());
      values.push(v);
    }
  }
  return values;
}

export class OrderRationalsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      const count = family === "F2" ? 4 : family === "F3" ? (rng.bool() ? 3 : 4) : 3;
      const values = distinctRationals(rng, cfg.denoms, count);
      if (values.length < count) continue; // not enough distinct values, retry

      // Decide display form per value.
      // F1: all fractions. F3: alternate fraction/decimal. F2: fractions.
      const displays = values.map((v, i) => {
        if (family === "F3") return i % 2 === 0 ? v.toString() : decStr(v);
        return v.toString();
      });

      // Scramble the presentation order (answer is always ascending).
      const idx = rng.shuffle(values.map((_, i) => i));
      const shownDisplays = idx.map((i) => displays[i] as string);

      const answer = makeSetAnswer(values);
      // Guard: ensure the set kept all values (distinct by construction).
      if (answer.values.length !== count) continue;

      const sortedLatex = answer.values.map((v) => v.toLatex()).join(" < ");

      const promptText = `Order these numbers from least to greatest: ${shownDisplays.join(", ")}`;

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: {
          text: promptText,
          latex: shownDisplays.join(",\\; "),
        },
        answerType: "set",
        answer,
        steps: [
          {
            description:
              "Rewrite each number in the same form (all decimals or a common denominator), then arrange from least to greatest.",
            latex: sortedLatex,
          },
        ],
        hints: [
          { level: 1, text: "Convert every number to the same form so they are easy to compare." },
          {
            level: 2,
            text: "Decimals compare place by place; fractions compare with a common denominator.",
          },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure =
      q.answer.kind === "set"
        ? q.answer.values.map((v) => v.key()).join("|")
        : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const orderRationals = new OrderRationalsGenerator();
